import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { AuthCard } from '@/components/auth/auth-card';
import { AuthField } from '@/components/auth/auth-field';
import { AuthForm } from '@/components/auth/auth-form';
import { completeRecoveryFn } from '@/server/functions/auth';

const searchSchema = z.object({
    userId: z.string().optional(),
    secret: z.string().optional(),
});

export const Route = createFileRoute('/_auth/recover_/reset')({
    component: RecoverResetPage,
    validateSearch: searchSchema,
});

const resetSchema = z
    .object({
        password: z.string().min(8, 'הסיסמה חייבת להכיל לפחות 8 תווים'),
        confirm: z.string().min(8, 'נא לאמת את הסיסמה'),
    })
    .refine((data) => data.password === data.confirm, {
        message: 'אימות הסיסמה אינו תואם',
        path: ['confirm'],
    });

function RecoverResetPage() {
    const search = Route.useSearch();
    const navigate = useNavigate();
    const [done, setDone] = useState(false);
    const form = useForm({
        resolver: zodResolver(resetSchema),
        defaultValues: {
            password: '',
            confirm: '',
        },
    });

    const resetMutation = useMutation({
        mutationFn: async (data: z.infer<typeof resetSchema>) => {
            if (!search.userId || !search.secret) {
                throw new Error('הקישור לאיפוס חסר או לא תקין');
            }
            await completeRecoveryFn({
                data: {
                    userId: search.userId,
                    secret: search.secret,
                    password: data.password,
                },
            });
        },
        onSuccess: () => {
            setDone(true);
            window.setTimeout(() => {
                void navigate({ to: '/sign-in' });
            }, 1500);
        },
        onError: (error: Error) => {
            form.setError('root', { message: error.message });
        },
    });

    if (!search.userId || !search.secret) {
        return (
            <AuthCard
                title="הקישור אינו תקף"
                description="ייתכן שהקישור לאיפוס פג תוקף או שכבר נעשה בו שימוש."
                footer={
                    <Link
                        to="/recover"
                        className="!no-underline hover:underline"
                    >
                        בקשת קישור חדש
                    </Link>
                }
            >
                <p className="text-sm text-white/55">
                    חסרים פרטים בכתובת. בקשו קישור חדש לאיפוס הסיסמה.
                </p>
            </AuthCard>
        );
    }

    return (
        <AuthCard
            title="בחירת סיסמה חדשה"
            description="הזינו סיסמה חדשה לחשבונכם."
            footer={
                <Link to="/sign-in" className="!no-underline hover:underline">
                    חזרה להתחברות
                </Link>
            }
        >
            {done ? (
                <p className="text-sm text-white/55">
                    הסיסמה עודכנה בהצלחה. מעבירים אתכם להתחברות…
                </p>
            ) : (
                <AuthForm
                    schema={resetSchema}
                    defaultValues={{ password: '', confirm: '' }}
                    onSubmit={(data) => resetMutation.mutate(data)}
                    submitText="עדכון סיסמה"
                    isLoading={resetMutation.isPending}
                    form={form}
                >
                    {(f) => (
                        <>
                            <AuthField
                                control={f.control}
                                name="password"
                                label="סיסמה חדשה"
                                placeholder="לפחות 8 תווים"
                                type="password"
                            />
                            <AuthField
                                control={f.control}
                                name="confirm"
                                label="אימות סיסמה"
                                placeholder="הזינו שוב את הסיסמה"
                                type="password"
                            />
                        </>
                    )}
                </AuthForm>
            )}
        </AuthCard>
    );
}
